import React, { FC, ReactNode, useEffect, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import { useAppSelector } from '@app/hooks/useReduxToolkit';
import { QuicksightState, UserState } from 'state-services';
import { useActivePartnerEntity } from '@app/hooks/useActivePartnerEntity';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { hasCapabilityPermission } from '@app/shared/permissions';
import { UserCapabilities } from '@app/shared/constants';
import { EXTERNAL_ROUTES } from 'type-declarations';
import InsightsIcon from '@mui/icons-material/Insights';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { Button, ButtonGroup, ButtonProps, Divider } from '@mui/material';
import LogoBanner from 'rhyme-component-library/src/assets/rhyme_logo.png';
import { UserMenu } from './UserMenu';

type page = {
	text: string;
	icon: ReactNode;
	path: string;
	matchPath?: string;
	visible: boolean;
	isSelected: boolean;
};

// 1. Extend ButtonProps to include your custom `selected` prop
interface NavButtonProps extends ButtonProps {
	selected?: boolean;
}

const TopNavbar: FC = () => {
	const [selectedPage, setSelectedPage] = useState('Lighthouse');
	const { activeScope } = useAppSelector<UserState>((state) => state.user);
	const { dashboard } = useAppSelector<QuicksightState>((state) => state.quicksight);
	const { capabilities } = activeScope;
	const activePartnerEntity = useActivePartnerEntity();

	const navigate = useNavigate();
	const currentPath = useLocation().pathname;

	const handleNavigation = (page: page): void => {
		if (page.text.includes('Case Search')) {
			window.location.href = page.path;
		} else {
			navigate(page.path);
			setSelectedPage(page.text);
		}
	};

	useEffect(() => {
		if (dashboard !== '') {
			setSelectedPage(dashboard);
		} else {
			const pagePath = pages.find((page) => currentPath.includes(page.path));
			const firstAvailablePage = pages.find((page) => page.visible);
			pagePath && pagePath.visible
				? setSelectedPage(pagePath.text)
				: firstAvailablePage
					? handleNavigation(firstAvailablePage)
					: null;
		}
	}, [activePartnerEntity, dashboard]);

	const pages: Array<page> = [
		{
			text: 'Lighthouse',
			icon: <InsightsIcon />,
			path: '/lighthouse',
			visible:
				hasCapabilityPermission(capabilities, [UserCapabilities.GetLighthouseDashboard]) &&
				activePartnerEntity.isLighthouseEnabled,
			isSelected: false,
		},
		{
			text: 'Gold Card',
			icon: <InsightsIcon />,
			path: '/goldcard',
			visible:
				hasCapabilityPermission(capabilities, [UserCapabilities.GetGoldCardDashboard]) &&
				activePartnerEntity.isGoldCardEnabled,
			isSelected: false,
		},
		{
			text: 'Case Search',
			icon: <ContentPasteSearchIcon />,
			path: EXTERNAL_ROUTES.ReactTachyon,
			visible: hasCapabilityPermission(capabilities, [UserCapabilities.GetCase]),
			isSelected: false,
		},
		{
			text: 'Admin',
			icon: <AdminPanelSettingsIcon />,
			path: '/admin/users',
			matchPath: '/admin',
			visible: hasCapabilityPermission(capabilities, [
				UserCapabilities.GrantUserRole,
				UserCapabilities.GetUser,
			]),
			isSelected: false,
		},
	];
	pages.forEach((page) => (page.isSelected = currentPath.includes(page.matchPath ?? page.path)));

	// 2. Use generic in `styled` to accept NavButtonProps
	const NavButton = styled(({ selected, ...props }: NavButtonProps) => (
		<Button {...props} />
	))<NavButtonProps>(({ selected, theme }) => ({
		fontSize: '1rem',
		boxShadow: 'none',
		color: selected ? 'var(--rhyme-accessible-blue)' : 'black',
		backgroundColor: 'transparent',
		textTransform: 'none',
		border: 0,
		margin: '1rem',
		borderRadius: 0,
		borderBottom: selected ? `2px solid var(--rhyme-accessible-blue)` : '2px solid transparent',
		fontWeight: selected ? 'bold' : 'normal',
		'&:hover': {
			borderBottom: `2px solid ${theme.palette.grey[300]}`,
			backgroundColor: 'transparent',
			color: '#757575',
		},
	}));

	return (
		<AppBar
			position="static"
			sx={{
				backgroundColor: 'transparent',
			}}
		>
			<Toolbar sx={{ padding: '1rem' }}>
				<Box
					justifyContent="center"
					alignItems="center"
					gap={5}
					display="flex"
					sx={{ overflow: 'auto' }}
				>
					<Link to="/">
						<img src={LogoBanner} alt="Return to Home" width="180px" />
					</Link>
					<ButtonGroup>
						{pages
							.filter((p) => p.visible)
							.map((page, index) => {
								return (
									<>
										<NavButton
											key={index}
											selected={selectedPage === page.text}
											onClick={() => handleNavigation(page)}
										>
											{page.text.toUpperCase()}
										</NavButton>
										<Divider orientation="vertical" flexItem sx={{ margin: '1rem' }} />
									</>
								);
							})}
					</ButtonGroup>
				</Box>
				<Box sx={{ flexGrow: 1 }} />
				<Box padding={1}>
					<UserMenu />
				</Box>
			</Toolbar>
		</AppBar>
	);
};

export default TopNavbar;
